import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import auth from '../helper/auth'; 
import config from '../config';
import PricingPlanMultipleColor from './child/PricingPlanMultipleColor'; 
import { toast } from 'react-toastify';
import { formatDisplayDate } from '../utils/formatDate';

const MySubscriptionLayer = () => {
    const [subscription, setSubscription] = useState(null);
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('overview'); // 'overview' or 'plans'

    const fetchSubscription = async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/subscription`);
            const data = await response.json();
            setSubscription(data.subscription);
            setUsage(data.usage);
        } catch (error) {
            console.error('Error fetching subscription:', error);
            toast.error('Failed to load subscription details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubscription();
    }, []);

    const handleCancel = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription?')) return;

        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/cancel-subscription`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ subscriptionId: subscription.id })
            });
            const data = await response.json();
            if (response.ok) {
                toast.success('Subscription canceled successfully');
                fetchSubscription();
            } else {
                toast.error(data.error || 'Failed to cancel subscription');
            }
        } catch (error) {
            console.error('Error canceling:', error);
            toast.error('An error occurred');
        }
    };

    const handleUpdatePlan = async (plan) => {
        if (window.confirm(`Switch to ${plan.name}? This will update your subscription immediately.`)) {
            try {
                const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/update-subscription`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        subscriptionId: subscription.id,
                        newPriceId: plan.stripe_price_id 
                    })
                });
                const data = await response.json();
                if (response.ok) {
                     // Check for redirect URL (SCA / Payment required)
                     if (data.approvalUrl) {
                         console.log('[DEBUG] Payment required. Redirecting to:', data.approvalUrl);
                         toast.info('Redirecting to payment gateway...');
                         window.location.href = data.approvalUrl;
                         return;
                     }

                     // Verify status
                     const subStatus = data.subscription?.status || data.status; // Fallback if subscription object is missing
                     if (subStatus === 'active' || subStatus === 'trialing') {
                        toast.success('Plan updated successfully');
                        setView('overview');
                        fetchSubscription();
                     } else {
                        toast.warning(`Subscription updated but status is ${subStatus}. Check payment details.`);
                        fetchSubscription(); // Still fetch to show latest state
                     }
                } else {
                    toast.error(data.error || 'Failed to update plan');
                }
            } catch (error) {
                console.error('Error updating plan:', error);
                toast.error('An error occurred');
            }
        }
    };

    if (loading) return <div className="text-center p-5">Loading subscription details...</div>;

    // If no active subscription or user wants to change plan
    if (!subscription || view === 'plans') {
        return (
            <div>
                {subscription && (
                    <button 
                        onClick={() => setView('overview')} 
                        className="btn btn-sm btn-outline-secondary radius-8 px-14 py-8 d-inline-flex align-items-center gap-2 mb-20 shadow-xs"
                    >
                        <Icon icon="solar:arrow-left-linear" className="text-base" />
                        <span>Back to My Subscription</span>
                    </button>
                )}
                <PricingPlanMultipleColor onSubscribe={subscription ? handleUpdatePlan : undefined} />
            </div>
        );
    }

    const isActiveSub = subscription.status === 'active' || subscription.status === 'trialing';

    // Subscription Overview
    return (
        <div className="hs-card">
            <div className="hs-card-header">
                <div className="d-flex align-items-center gap-2">
                    <Icon icon="solar:crown-star-bold" className="text-warning-600 text-xl" />
                    <h6 className="fw-bold mb-0 text-dark">My Subscription</h6>
                </div>
                <span className={`hs-badge ${isActiveSub ? 'hs-badge-success' : 'hs-badge-danger'}`}>
                    <Icon icon={isActiveSub ? 'solar:check-circle-bold' : 'solar:close-circle-bold'} className="text-xs" />
                    {subscription.status.toUpperCase()}
                </span>
            </div>
            <div className="hs-card-body p-24">
                <div className="row g-3">
                    <div className="col-lg-6">
                        <div className="p-20 border rounded-12 bg-neutral-50 h-100">
                            <span className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-4">Active Plan Tier</span>
                            <h4 className="fw-bold text-dark mb-2">{subscription.plan_name}</h4>
                            <p className="text-primary-700 fw-bold mb-0 text-md">
                                {subscription.currency.toUpperCase()} {subscription.price} <small className="text-secondary-light text-xs font-normal">/ {subscription.interval || 'month'}</small>
                            </p>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="p-20 border rounded-12 bg-neutral-50 h-100">
                            <span className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-4">Billing & Renewal</span>
                            <h4 className="fw-bold text-dark mb-2">
                                {subscription.current_period_end
                                    ? formatDisplayDate(subscription.current_period_end)
                                    : 'N/A'}
                            </h4>
                            <p className="text-secondary-light text-xs mb-0">
                                Automatic renewal is <strong className={subscription.cancel_at_period_end || !isActiveSub ? "text-danger-600" : "text-success-600"}>{subscription.cancel_at_period_end || !isActiveSub ? 'INACTIVE' : 'ACTIVE'}</strong>
                            </p>
                        </div>
                    </div>
                    
                    <div className="col-12 mt-16">
                        <div className="p-20 border rounded-12 bg-white">
                            <h6 className="fw-bold text-dark mb-16 d-flex align-items-center gap-2">
                                <Icon icon="solar:chart-2-bold" className="text-primary-600" />
                                Resource Allocation & Quotas
                            </h6>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="d-flex justify-content-between mb-8">
                                        <span className="text-secondary-light text-sm fw-medium">Venues Capacity</span>
                                        <span className="fw-bold text-dark text-sm">{usage.venues} / {usage.max_venues}</span>
                                    </div>
                                    <div className="progress" style={{ height: '8px', borderRadius: '6px' }}>
                                        <div 
                                            className="progress-bar hs-progress-gradient" 
                                            role="progressbar" 
                                            style={{ width: `${Math.min((usage.venues / Math.max(usage.max_venues, 1)) * 100, 100)}%`, borderRadius: '6px' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex justify-content-between mb-8">
                                        <span className="text-secondary-light text-sm fw-medium">Features & Amenities</span>
                                        <span className="fw-bold text-dark text-sm">Up to {usage.max_features} per venue</span>
                                    </div>
                                    <div className="progress" style={{ height: '8px', borderRadius: '6px' }}>
                                        <div 
                                            className="progress-bar hs-progress-gradient" 
                                            role="progressbar" 
                                            style={{ width: '100%', borderRadius: '6px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 mt-20 d-flex gap-3 flex-wrap">
                        <button 
                            onClick={() => setView('plans')} 
                            className="hs-btn-primary"
                        >
                            <Icon icon="solar:crown-star-bold" />
                            Upgrade / Change Plan
                        </button>
                        {subscription.status === 'active' && !subscription.cancel_at_period_end && (
                            <button 
                                onClick={handleCancel} 
                                className="hs-btn-secondary text-danger-600 border-danger-subtle"
                            >
                                Cancel Subscription
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MySubscriptionLayer;
